import { useEffect, useMemo, useState } from 'react'

import { NextPage } from 'next'
import Head from 'next/head'
import { useRouter } from 'next/router'

import InfiniteScroll from 'react-infinite-scroll-component'

import { useFoodSWR, useFoodSWRInfinite } from '~/api-client/food_api'
import { flattenOpt } from '~/api-client/food_api/array'
import { BottoFloatActionButton } from '~/components/action/BottomFloatingActionButton'
import {
  TweetDialog,
  TweetDialogVariant,
} from '~/components/food_app/tweet/TweetDialog'
import { TweetImageDialog } from '~/components/food_app/tweet/TweetImageDialog'
import { TweetRow } from '~/components/food_app/tweet/TweetRow'
import { Container } from '~/components/layout/Container'
import { Footer } from '~/components/layout/Footer'
import { Header } from '~/components/layout/Header'
import { config } from '~/config'
import { optLast } from '~/libs/array'
import { TweetOrReply } from '~/libs/tweet'
import { useApplication } from '~/provider/ApplicationProvider'

const Page: NextPage = () => {
  const router = useRouter()

  const app = useApplication()
  const [selectedImage, setSelectedImage] = useState<{
    tweetOrReply: TweetOrReply
    index: number
  }>()
  const [tweetDialogVariant, setTweetDialogVariant] =
    useState<TweetDialogVariant>()

  const userMe = useFoodSWR('/user/me', {})

  const tweets = useFoodSWRInfinite(
    '/tweet/tweets',
    {},
    (_, previousPageData) => {
      return {
        count: config.pageSize.tweetList,
        cursor_next: optLast(previousPageData?.tweets)?.tweetId,
      }
    }
  )

  const filteredTweets = useMemo(
    () =>
      // NOTE: 削除済みを表示するかどうかフィルター。条件に微調整が入る可能性があるため、一旦フロント対応にしている。（APIで互換実装されたら削除できる）
      flattenOpt(tweets.data?.map((t) => t.tweets))?.filter(
        (t) =>
          t.state === 'active' ||
          t.replies.length > 0 ||
          t.receivedGifts.length > 0
      ),
    [tweets]
  )

  useEffect(() => {
    ;(async () => {
      if (typeof router.query.scroll_to === 'string') {
        const el = document.querySelector(
          `[data-tweet-id="${router.query.scroll_to}"]`
        )

        if (el) {
          router.replace(
            {
              pathname: '/tweets',
            },
            {},
            { shallow: true }
          )

          el.scrollIntoView({
            block: 'start',
          })
          await tweets.mutate()
        }
      }
    })()
  }, [router, tweets])

  return (
    <div className="min-h-full">
      <Head>
        <title>つぶやき</title>
      </Head>

      <TweetDialog
        userMe={userMe.data?.user}
        variant={tweetDialogVariant}
        onSuccess={async () => {
          if (tweetDialogVariant == null) return

          await tweets.mutate()
          setTweetDialogVariant(undefined)
        }}
        onClose={() => {
          setTweetDialogVariant(undefined)
        }}
      />

      <TweetImageDialog
        selectedImage={selectedImage}
        onClose={() => setSelectedImage(undefined)}
      />

      <div>
        <Header>
          <div className="w-5" />
          つぶやき
          <div className="w-5" />
        </Header>
        <Container>
          <div className="">
            {filteredTweets?.length === 0 && (
              <div className="mx-4 md:mx-0 text-xs text-center leading-5 mt-[30vh]">
                <p>
                  つぶやきがありません。
                  <br />
                  新しくぶつやきを投稿したい場合は、
                  <br />
                  右下の＋ボタンから作成できます。
                </p>
              </div>
            )}

            <InfiniteScroll
              className="flex flex-col w-full py-1 mx-auto"
              dataLength={filteredTweets?.length ?? 0}
              next={() => {
                tweets.setSize((size) => size + 1)
              }}
              hasMore={
                (tweets.data &&
                  tweets.data[tweets.data.length - 1]?.tweets.length !== 0) ??
                false
              }
              loader={<div className="loader" key={0} />}
            >
              {filteredTweets?.map((tweet) => {
                return (
                  <TweetRow
                    key={tweet.tweetId}
                    tweetOrReply={{ type: 'tweet', value: tweet }}
                    userMe={userMe.data?.user}
                    onClickImage={(tweetOrReply, index) => {
                      setSelectedImage({
                        tweetOrReply,
                        index,
                      })
                    }}
                    onEdit={(tweetOrReply) => {
                      switch (tweetOrReply.type) {
                        case 'tweet':
                          setTweetDialogVariant({
                            type: 'updateTweet',
                            resource: tweetOrReply.value,
                          })
                          break
                        case 'reply':
                          setTweetDialogVariant({
                            type: 'updateReply',
                            resource: tweetOrReply.value,
                          })
                          break
                      }
                    }}
                    onReplyTo={(tweetOrReply) => {
                      setTweetDialogVariant({
                        type: 'newReply',
                        replyTo: tweetOrReply,
                      })
                    }}
                    onGiftTo={(tweetOrReply) => {
                      switch (tweetOrReply.type) {
                        case 'tweet':
                          switch (app.paymentType) {
                            case 'iap':
                              app.delegateSaleGiftsForTweet(
                                tweetOrReply.value.tweetId,
                                async () => {
                                  await tweets.mutate()
                                }
                              )
                              break
                            case 'web':
                            case 'unsupported':
                              router.push(
                                `/tweets/${tweetOrReply.value.tweetId}/gifts`
                              )
                              break
                          }
                          break
                        case 'reply':
                          switch (app.paymentType) {
                            case 'iap':
                              app.delegateSaleGiftsForReply(
                                tweetOrReply.value.tweetReplyId,
                                async () => {
                                  await tweets.mutate()
                                }
                              )
                              break
                            case 'web':
                            case 'unsupported':
                              router.push(
                                `/tweets/${tweetOrReply.value.parentTweetId}/gifts?tweet_reply_id=${tweetOrReply.value.tweetReplyId}`
                              )
                              break
                          }
                          break
                      }
                    }}
                  />
                )
              })}
            </InfiniteScroll>
          </div>
        </Container>

        <BottoFloatActionButton
          onClick={() => setTweetDialogVariant({ type: 'newTweet' })}
        />
        {tweetDialogVariant == null && <Footer />}
      </div>
    </div>
  )
}

export default Page
